/**
 * Countdown discount banner.
 *
 * On a successful add-to-cart, starts a configurable timer (default 10 min).
 * Shows a floating top bar with mm:ss remaining. While the timer is active,
 * any link or button click that goes to /checkout gets ?discount=CODE
 * appended so Shopify auto-applies the discount at checkout.
 *
 * Config (set via inline <script> before this file loads):
 *   window.__discoCountdownConfig = {
 *     code: 'DiscoCountdown',
 *     percent: 10,
 *     minutes: 10,
 *     resetOnEachAdd: true,
 *     messageActive: '🔥 {percent}% OFF unlocked — applied at checkout',
 *     messageExpiring: '⏰ Hurry — {percent}% OFF ends soon',
 *     messageExpired: '⌛ Discount expired',
 *   };
 *
 * Public API (rare — usually fully automatic):
 *   window.DiscoCountdown.start()        // manually start/reset the timer
 *   window.DiscoCountdown.stop()         // clear and hide
 *   window.DiscoCountdown.isActive()     // boolean
 *   window.DiscoCountdown.remainingMs()  // number
 */
(function () {
  var CFG = (window.__discoCountdownConfig || {});
  var CODE = String(CFG.code || 'DiscoCountdown');
  var PERCENT = Number(CFG.percent || 10);
  var MINUTES = Number(CFG.minutes || 10);
  var DURATION_MS = MINUTES * 60 * 1000;
  var EXPIRING_THRESHOLD_MS = 60 * 1000; // last minute pulses
  var POST_EXPIRY_DISPLAY_MS = 5 * 1000;
  var RESET_ON_EACH_ADD = CFG.resetOnEachAdd !== false;
  var STORAGE_KEY = 'discoCountdown.expiresAt';

  var MSG_ACTIVE = String(CFG.messageActive || '🔥 {percent}% OFF unlocked — applied at checkout');
  var MSG_EXPIRING = String(CFG.messageExpiring || '⏰ Hurry — {percent}% OFF ends soon');
  var MSG_EXPIRED = String(CFG.messageExpired || '⌛ Discount expired');

  function format(tpl) {
    return tpl
      .replace(/\{code\}/g, CODE)
      .replace(/\{percent\}/g, String(PERCENT))
      .replace(/\{minutes\}/g, String(MINUTES));
  }

  function readExpiry() {
    try {
      var v = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
      return isNaN(v) ? 0 : v;
    } catch (e) { return 0; }
  }
  function writeExpiry(ts) {
    try {
      if (ts) localStorage.setItem(STORAGE_KEY, String(ts));
      else localStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function remainingMs() {
    var expiry = readExpiry();
    if (!expiry) return 0;
    return Math.max(0, expiry - Date.now());
  }

  function isActive() { return remainingMs() > 0; }

  function fmtTime(ms) {
    var s = Math.max(0, Math.ceil(ms / 1000));
    var m = Math.floor(s / 60);
    var r = s % 60;
    return m + ':' + (r < 10 ? '0' + r : r);
  }

  // ---------- Banner DOM ----------

  var bannerEl, timerEl, expiredTimeoutId;

  function ensureBanner() {
    if (bannerEl) return bannerEl;
    bannerEl = document.createElement('div');
    bannerEl.className = 'disco-countdown';
    bannerEl.setAttribute('role', 'status');
    bannerEl.setAttribute('aria-live', 'polite');
    bannerEl.hidden = true;
    bannerEl.innerHTML =
      '<span class="disco-countdown__msg" data-disco-msg></span>' +
      '<span class="disco-countdown__sep" aria-hidden="true">·</span>' +
      '<span class="disco-countdown__time" data-disco-time></span>';
    document.body.appendChild(bannerEl);
    timerEl = bannerEl.querySelector('[data-disco-time]');
    return bannerEl;
  }

  function setMessage(msg, time) {
    ensureBanner();
    bannerEl.querySelector('[data-disco-msg]').textContent = format(msg);
    if (time != null) timerEl.textContent = time;
  }

  function showActiveBanner() {
    ensureBanner();
    bannerEl.hidden = false;
    bannerEl.classList.remove('disco-countdown--expired');
    document.body.classList.add('has-disco-countdown');
  }

  function hideBanner() {
    if (!bannerEl) return;
    bannerEl.hidden = true;
    bannerEl.classList.remove('disco-countdown--expired', 'disco-countdown--expiring');
    document.body.classList.remove('has-disco-countdown');
  }

  // ---------- Tick loop ----------

  var rafId = null;
  function tick() {
    var ms = remainingMs();
    if (ms <= 0) {
      handleExpiry();
      return;
    }
    showActiveBanner();
    bannerEl.classList.toggle('disco-countdown--expiring', ms <= EXPIRING_THRESHOLD_MS);
    setMessage(ms <= EXPIRING_THRESHOLD_MS ? MSG_EXPIRING : MSG_ACTIVE, fmtTime(ms));
    // Schedule next tick at next whole-second boundary
    var nextDelay = Math.max(50, ms % 1000 || 1000);
    rafId = window.setTimeout(tick, nextDelay);
  }

  function handleExpiry() {
    if (rafId) { clearTimeout(rafId); rafId = null; }
    writeExpiry(0);
    if (!bannerEl) return;
    bannerEl.classList.add('disco-countdown--expired');
    bannerEl.classList.remove('disco-countdown--expiring');
    setMessage(MSG_EXPIRED, '');
    if (expiredTimeoutId) clearTimeout(expiredTimeoutId);
    expiredTimeoutId = setTimeout(hideBanner, POST_EXPIRY_DISPLAY_MS);
  }

  function start() {
    var expiry = Date.now() + DURATION_MS;
    writeExpiry(expiry);
    if (rafId) { clearTimeout(rafId); rafId = null; }
    if (expiredTimeoutId) { clearTimeout(expiredTimeoutId); expiredTimeoutId = null; }
    tick();
  }

  function maybeResume() {
    if (isActive()) tick();
  }

  // ---------- Checkout URL injection ----------

  function withDiscount(url) {
    try {
      var u = new URL(url, window.location.origin);
      // Only touch checkout-bound paths
      if (!/(^|\/)checkout(\/|$|\?)/.test(u.pathname)) return url;
      if (!isActive()) return url;
      u.searchParams.set('discount', CODE);
      return u.pathname + (u.search || '') + (u.hash || '');
    } catch (e) {
      return url;
    }
  }

  function isCheckoutAnchor(el) {
    if (!el || el.tagName !== 'A') return false;
    var href = el.getAttribute('href') || '';
    return /(^|\/)checkout(\/|$|\?)/.test(href);
  }

  function isCheckoutButton(el) {
    if (!el) return false;
    if (el.name === 'checkout' || el.getAttribute('name') === 'checkout') return true;
    var role = (el.getAttribute('data-checkout') || '').toLowerCase();
    if (role === 'true' || role === '1') return true;
    return false;
  }

  document.addEventListener('click', function (e) {
    if (!isActive()) return;
    var t = e.target;
    while (t && t !== document) {
      if (isCheckoutAnchor(t)) {
        var newHref = withDiscount(t.getAttribute('href'));
        if (newHref !== t.getAttribute('href')) t.setAttribute('href', newHref);
        return;
      }
      if (isCheckoutButton(t)) {
        // Intercept the post-to-/cart-then-redirect flow by sending the user
        // directly to /checkout?discount=...
        e.preventDefault();
        window.location.href = withDiscount('/checkout');
        return;
      }
      t = t.parentNode;
    }
  }, true);

  // ---------- Cart add hook (patch fetch) ----------

  var origFetch = window.fetch;
  if (typeof origFetch === 'function') {
    window.fetch = function (input, init) {
      var url = '';
      try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        promise.then(function (res) {
          if (res && res.ok && (RESET_ON_EACH_ADD || !isActive())) {
            start();
          }
        }).catch(function () {});
      }
      return promise;
    };
  }

  // Also patch XMLHttpRequest for older add-to-cart paths
  try {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__discoUrl = url || '';
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var self = this;
      var url = self.__discoUrl || '';
      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        self.addEventListener('load', function () {
          if (self.status >= 200 && self.status < 300 && (RESET_ON_EACH_ADD || !isActive())) {
            start();
          }
        });
      }
      return origSend.apply(self, arguments);
    };
  } catch (e) {}

  // ---------- Public API ----------

  window.DiscoCountdown = {
    start: start,
    stop: function () { writeExpiry(0); hideBanner(); },
    isActive: isActive,
    remainingMs: remainingMs,
    code: CODE,
    percent: PERCENT,
  };

  // ---------- Boot ----------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      ensureBanner();
      maybeResume();
    });
  } else {
    ensureBanner();
    maybeResume();
  }
})();
